import type {
  StrapiLink,
  StrapiMediaFile,
  StrapiSkillCategory,
} from '../wire/common.js';
import type {
  StrapiEducationBase,
  StrapiProjectBase,
  StrapiSkillBase,
} from '../wire/base.js';
import type { StrapiProfileResponse } from '../wire/profile.js';
import type { StrapiAwardResponse } from '../wire/award.js';
import type { StrapiCertificationResponse } from '../wire/certification.js';
import type { StrapiExperienceResponse } from '../wire/experience.js';
import type {
  Award,
  Certification,
  Education,
  Experience,
  LinkResponse,
  MediaFile,
  Profile,
  Project,
  Skill,
  SkillCategoryResponse,
} from '../domain/types.js';

export interface MapperContext {
  mediaBaseUrl: string;
}

// CMS dates are plain `YYYY-MM-DD` strings, which `new Date()` parses as UTC
// midnight. Formatting must therefore also happen in UTC, or viewers west of
// Greenwich see the previous month/year.
export const formatMonthYear = (date?: string | null): string | undefined => {
  if (!date) return undefined;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatDateRange = (
  startDate: string,
  endDate?: string | null,
): string =>
  `${formatMonthYear(startDate) ?? ''} - ${formatMonthYear(endDate) ?? 'Present'}`;

export const mapMediaFile = (
  media: StrapiMediaFile,
  ctx: MapperContext,
): MediaFile | undefined => {
  const url = media.url.trim();
  if (!url) return undefined;
  return {
    id: media.id,
    name: media.name,
    url: /^https?:\/\//i.test(url)
      ? url
      : `${ctx.mediaBaseUrl.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`,
    alternativeText: media.alternativeText ?? undefined,
  };
};

const mapOptionalMedia = (
  media: StrapiMediaFile | null | undefined,
  ctx: MapperContext,
): MediaFile | undefined => (media ? mapMediaFile(media, ctx) : undefined);

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

export const mapSkillCategory = (
  category: StrapiSkillCategory,
): SkillCategoryResponse => ({
  id: category.id,
  name: category.name,
  color: category.color ?? undefined,
  icon: category.icon ?? undefined,
});

export const mapLink = (link: StrapiLink): LinkResponse => ({
  id: link.id,
  label: link.label,
  isVisible: link.isVisible ?? undefined,
  url: link.url,
  type: link.type,
  showIcon: link.showIcon ?? undefined,
  isExternal: link.isExternal ?? undefined,
  description: link.description ?? undefined,
});

export const mapProfile = (
  profile: StrapiProfileResponse,
  ctx: MapperContext,
): Profile => ({
  id: profile.id,
  name: profile.name,
  slug: profile.slug,
  headline: profile.headline ?? undefined,
  summary: profile.summary ?? undefined,
  shortBio: profile.shortBio ?? undefined,
  avatar: mapOptionalMedia(profile.avatar, ctx),
  coverImage: mapOptionalMedia(profile.coverImage, ctx),
  location: profile.location ?? undefined,
  timezone: profile.timezone ?? undefined,
  website: profile.website ?? undefined,
  dateOfBirth: profile.dateOfBirth ?? undefined,
  languages: profile.languages,
  interests: profile.interests,
  email: profile.email,
  phone: profile.phone ?? undefined,
  socialLinks: profile.socialLinks?.map(mapLink) ?? undefined,
  resume: mapOptionalMedia(profile.resume, ctx),
});

// Accepts the base shape so it also works on relation items nested inside
// other entities, where `categories` is not populated.
export const mapSkill = (skill: StrapiSkillBase): Skill => ({
  id: skill.id,
  name: skill.name,
  slug: skill.slug,
  description: skill.description ?? undefined,
  categories: skill.categories?.map(mapSkillCategory) ?? [],
  tags: skill.tags ?? undefined,
  proficiencyLevel: skill.proficiencyLevel ?? undefined,
  yearsOfExperience: skill.yearsOfExperience ?? undefined,
  icon: skill.icon ?? undefined,
  link: skill.link ?? undefined,
  isActive: skill.isActive,
  isFeatured: skill.isFeatured,
});

export const mapEducation = (
  education: StrapiEducationBase,
  ctx: MapperContext,
): Education => ({
  id: education.id,
  slug: education.slug ?? String(education.id),
  institution: education.institution,
  degree: education.degree,
  fieldOfStudy: education.fieldOfStudy,
  level: education.level,
  startDate: education.startDate,
  endDate: education.endDate ?? undefined,
  location: education.location ?? undefined,
  gpa: education.gpa ?? undefined,
  description: education.description ?? undefined,
  summary: education.summary ?? undefined,
  achievements: education.achievements,
  logo: mapOptionalMedia(education.logo, ctx),
  diploma: mapOptionalMedia(education.diploma, ctx),
  transcript: mapOptionalMedia(education.transcript, ctx),
  links: education.links?.map(mapLink) ?? undefined,
  isFeatured: education.isFeatured,
});

export const mapAward = (
  award: StrapiAwardResponse,
  ctx: MapperContext,
): Award => ({
  id: award.id,
  title: award.title,
  slug: award.slug,
  issuer: award.issuer,
  type: award.type,
  categories: award.categories.map(mapSkillCategory),
  date: award.date,
  description: award.description ?? undefined,
  summary: award.summary ?? undefined,
  criteria: award.criteria ?? undefined,
  impact: award.impact ?? undefined,
  logo: mapOptionalMedia(award.logo, ctx),
  certificate: mapOptionalMedia(award.certificate, ctx),
  links: award.links?.map(mapLink) ?? undefined,
  isFeatured: award.isFeatured,
});

export const mapCertification = (
  certification: StrapiCertificationResponse,
  ctx: MapperContext,
): Certification => ({
  id: certification.id,
  name: certification.name,
  slug: certification.slug,
  issuer: certification.issuer,
  categories: certification.categories.map(mapSkillCategory),
  level: certification.level ?? undefined,
  issueDate: certification.issueDate,
  expiryDate: certification.expiryDate ?? undefined,
  credentialId: certification.credentialId ?? undefined,
  description: certification.description ?? undefined,
  summary: certification.summary ?? undefined,
  logo: mapOptionalMedia(certification.logo, ctx),
  certificate: mapOptionalMedia(certification.certificate, ctx),
  badge: mapOptionalMedia(certification.badge, ctx),
  links: certification.links?.map(mapLink) ?? undefined,
  isFeatured: certification.isFeatured,
  skills: certification.skills?.map(mapSkill) ?? undefined,
  education:
    certification.education?.map((item) => mapEducation(item, ctx)) ??
    undefined,
});

export const mapProject = (
  project: StrapiProjectBase,
  ctx: MapperContext,
): Project => ({
  id: project.id,
  title: project.title,
  slug: project.slug,
  description: project.description,
  summary: project.summary ?? undefined,
  type: project.type,
  status: project.currentStatus,
  startDate: project.startDate ?? undefined,
  endDate: project.endDate ?? undefined,
  coverImage: mapOptionalMedia(project.coverImage, ctx),
  gallery:
    project.gallery?.map((item) => mapMediaFile(item, ctx)).filter(isDefined) ??
    undefined,
  links:
    project.links?.map((link) => ({
      ...mapLink(link),
      isVisible: link.isVisible ?? true,
    })) ?? undefined,
  achievements: project.achievements ?? undefined,
  challenges: project.challenges ?? undefined,
  learnings: project.learnings ?? undefined,
  role: project.role ?? undefined,
  teamSize: project.teamSize ?? undefined,
  client: project.client ?? undefined,
  isFeatured: project.isFeatured,
  isPublic: project.isPublic,
});

export const mapExperience = (
  experience: StrapiExperienceResponse,
  ctx: MapperContext,
): Experience => ({
  id: experience.id,
  title: experience.title,
  slug: experience.slug,
  company: experience.company,
  type: experience.type,
  position: experience.position,
  startDate: experience.startDate,
  endDate: experience.endDate ?? undefined,
  industry: experience.industry ?? undefined,
  location: experience.location ?? undefined,
  workMode: experience.workMode ?? undefined,
  description: experience.description,
  summary: experience.summary ?? undefined,
  achievements: experience.achievements ?? undefined,
  categories: experience.categories.map(mapSkillCategory),
  logo: mapOptionalMedia(experience.logo, ctx),
  links: experience.links?.map(mapLink) ?? undefined,
  isFeatured: experience.isFeatured,
  projects:
    experience.projects?.map((project) => mapProject(project, ctx)) ?? [],
  duration: formatDateRange(
    experience.startDate,
    experience.endDate ?? undefined,
  ),
});
